/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useState, useRef } from 'react';
import {
  FieldArray,
  ArrayHelpers,
} from 'formik';
import {
  Button,
  Typography,
  Row,
  Col,
} from 'antd';
import {
  Form,
  Select,
  AutoComplete,
  DatePicker,
} from 'formik-antd';

import debounce from 'lodash.debounce';
import { DefaultOptionType } from 'antd/lib/select';
import { useStore } from '../../../../store';
import { IListsForReport, IReportQuickRange } from '../../../../store/modules/report/i-report';
import { IFilter } from '../../create-report/interfaces';
import { useActions } from '../../../providers/actions-provider/actions.context';
import { ReactComponent as ThrashCan } from '../../../../assets/icons/thrashCan.svg';
import { useFilterSortContext } from '../context/filter-sort-context';

const { Option } = Select;

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types

export function getQuickRangeByValue(
  ranges: IReportQuickRange[],
  value: string,
): IReportQuickRange | undefined {
  return ranges?.find((item) => item.value === value);
}

export function getQuickRangeByToAndFrom(
  ranges: IReportQuickRange[],
  from: string,
  to: string,
): IReportQuickRange | undefined {
  return ranges?.find((item) => item.from === from && item.to === to);
}

/* eslint-disable */
interface IFieldArrayRefProps {
  fieldArrayHelpers: ArrayHelpers;
  values: any;
  dirty: boolean;
  children?: React.ReactNode;
}

class FieldArrayRef extends React.Component<IFieldArrayRefProps> {
  addItem(value: any) {
    const { fieldArrayHelpers: { push } } = this.props;
    push(value);
  }

  isDirty() {
    return this.props.dirty;
  }

  render() {
    return (
      <>
        {this.props.children}
      </>
    );
  }
}
/* eslint-enable */

interface IReportParamsProps extends IListsForReport {
  submitButtonLabel: string;
  notEditable: boolean;
  submitButtonAlwaysEnabled?: boolean;
  reportCollection: string;
  renderAdditionalButtons?: () => JSX.Element;
}

const FilterComponent = ({
  quickRanges: reportQuickRanges,
  operators,
  filterFields,
  submitButtonLabel,
  submitButtonAlwaysEnabled,
  reportCollection,
  notEditable,
  renderAdditionalButtons,
}: IReportParamsProps): JSX.Element => {
  const { reportActions } = useActions();
  const {
    reportFieldSuggestions,
  } = useStore();
  const { formik } = useFilterSortContext();

  const [filterSuggestions, setFilterSuggestions] = useState<DefaultOptionType[]>([]);

  // TODO: this is duplicated in filter-modal.tsx, move it where it can be reusable
  const debouncedSuggestionsSearch = debounce((searchText, fieldIndex, formikFilters) => {
    const data = reportFieldSuggestions.data[formikFilters[fieldIndex].field];
    const opts = (data || []).filter(
      (suggestion: { value: string; }) => (
        suggestion.value.toLowerCase().includes(searchText.toLowerCase())),
    );
    setFilterSuggestions(opts);
  }, 500);

  const filtersRef = useRef<FieldArrayRef>(null);

  const renderFiltersExtraButtons = (
    // eslint-disable-next-line
    values: any,
    // eslint-disable-next-line
    setFieldValue: (n: string, v: any) => void,
  ) => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      fontSize: '14px',
      fontWeight: 600,
      paddingTop: '10px',
      paddingBottom: '10px',
    }}
    >
      <Button
        style={{ padding: '0', display: 'flex', justifyContent: 'flex-start' }}
        disabled={notEditable}
        type="link"
        onClick={(event) => {
          event.stopPropagation();
          if (!values.displayTimeFilter) {
            setFieldValue('displayTimeFilter', true);
            setFieldValue('from', '');
            setFieldValue('to', '');
            setFieldValue('fromDate', '');
            setFieldValue('toDate', '');
            setFieldValue('quickRange', '');
          }
        }}
      >
        + Add new time filter
      </Button>
      <Button
        style={{ padding: '0', display: 'flex', justifyContent: 'flex-start' }}
        disabled={notEditable}
        type="link"
        onClick={(event) => {
          filtersRef.current?.addItem({
            filed: '',
            type: '',
            value: '',
          });
          event.stopPropagation();
        }}
      >
        + Add new filter
      </Button>
    </div>
  );

  const onDateSelected = (
    name: string,
    setFieldValue: (n: string, v: string) => void,
  ) => (
    /* eslint-disable-next-line */
    (value: any, dateString: string) => {
      setFieldValue(name, dateString);
      if (value) {
        setFieldValue('quickRange', '');
      }
    });

  const onQuickRangeSelected = (setFieldValue: (n: string, v: string) => void) => (
    (value: string) => {
      const qr = getQuickRangeByValue(reportQuickRanges, value);
      /* eslint-disable-next-line */
      if (qr) {
        setTimeout(() => {
          setFieldValue('from', qr.from);
          setFieldValue('to', qr.to);
          setFieldValue('fromDate', '');
          setFieldValue('toDate', '');
        }, 1);
      }
    }
  );

  /* eslint-disable-next-line */
  const defineSuggestions = (values: any, filterIdx: number) => (
    (value: string) => {
      if (!value || value.trim() === '') {
        const { data } = reportFieldSuggestions;
        const fieldData = (
          data[values.filters[filterIdx].field]
        );
        setFilterSuggestions(fieldData);
      }
    }
  );

  const {
    values, dirty, handleSubmit, setFieldValue, errors, touched,
  } = formik;

  return (
    <Form style={{ width: '100%' }}>
      <Row style={{ padding: '0 24px', overflow: 'scroll' }}>
        <Col span="24" style={{ height: 'calc(100vh - 150px)' }}>
          {values.displayTimeFilter && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              padding: '16px 0 32px 0',
              borderBottom: '1px solid #D4C8FA',
            }}
            >
              <div style={{
                display: 'flex',
                flexDirection: 'column',
              }}
              >
                <Typography.Text style={{
                  color: '#8c909b',
                  fontSize: '14px',
                  lineHeight: '17px',
                  marginBottom: '5px',
                }}
                >
                  From
                  {(touched.from && errors.from) ? (
                    <span style={{
                      color: 'red',
                      paddingLeft: '5px',
                    }}
                    >
                      {errors.from}
                    </span>
                  ) : ''}
                </Typography.Text>
                <DatePicker
                  disabled={notEditable}
                  name="fromDate"
                  showTime
                  onChange={onDateSelected('from', setFieldValue)}
                  showNow={false}
                  style={(touched.from && errors.from) ? { border: '1px solid red' } : {}}
                />
                {/* <Input
                    disabled={notEditable}
                    name="from"
                    className="report-filter__pseudo-input"
                  /> */}
              </div>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
              }}
              >
                <Typography.Text style={{
                  color: '#8c909b',
                  fontSize: '14px',
                  lineHeight: '17px',
                  marginBottom: '5px',
                }}
                >
                  To
                  {(touched.to && errors.to) ? (
                    <span style={{
                      color: 'red',
                      paddingLeft: '5px',
                    }}
                    >
                      {errors.to}
                    </span>
                  ) : ''}
                </Typography.Text>
                <DatePicker
                  disabled={notEditable}
                  name="toDate"
                  showTime
                  showNow={false}
                  onChange={onDateSelected('to', setFieldValue)}
                  style={(touched.to && errors.to) ? { border: '1px solid red' } : {}}
                />
                {/* <Input
                    disabled={notEditable}
                    name="to"
                    className="report-filter__pseudo-input"
                  /> */}
              </div>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
              }}
              >
                <Typography.Text style={{
                  color: '#8c909b',
                  fontSize: '14px',
                  lineHeight: '17px',
                  marginBottom: '5px',
                }}
                >
                  Quick ranges
                </Typography.Text>
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                  <Select
                    disabled={notEditable}
                    name="quickRange"
                    size="large"
                    className="ant__select"
                    style={{ width: '88%', height: 40 }}
                    onChange={onQuickRangeSelected(setFieldValue)}
                  >
                    {reportQuickRanges.length
                                && reportQuickRanges.map((item) => (
                                  <Option value={item.value} key={item.value}>
                                    {item.text}
                                  </Option>
                                ))}
                  </Select>

                  <button
                    disabled={notEditable}
                    type="button"
                    onClick={() => {
                      if (values.displayTimeFilter) {
                        // setTimeFilterVisible(false);
                        setFieldValue('displayTimeFilter', false);
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {notEditable ? '' : <ThrashCan /> }
                  </button>
                </div>

              </div>
            </div>
          )}
          <FieldArray name="filters">
            {(filtersArrayHelpers) => (
              <FieldArrayRef
                ref={filtersRef}
                fieldArrayHelpers={filtersArrayHelpers}
                values={values}
                dirty={dirty}
              >
                {values.filters && values.filters.length > 0
                        && values.filters.map((filter: IFilter, filterIdx: number) => {
                          // eslint-disable-next-line
                          const fErrors: any = errors.filters || [];
                          // eslint-disable-next-line
                          const fTouched: any = touched.filters || [];
                          const hasFieldError = (
                            fErrors[filterIdx] && !!fErrors[filterIdx].field
                            && fTouched[filterIdx] && !!fTouched[filterIdx].field
                          );
                          const hasTypeError = (
                            fErrors[filterIdx] && !!fErrors[filterIdx].type
                            && fTouched[filterIdx] && !!fTouched[filterIdx].type
                          );
                          const hasValueError = (
                            fErrors[filterIdx] && !!fErrors[filterIdx].value
                            && fTouched[filterIdx] && !!fTouched[filterIdx].value
                          );
                          return (
                            <div
                              style={{
                                padding: '16px 0 32px 0',
                                borderBottom: '1px solid #D4C8FA',
                                display: 'flex',
                                flexDirection: 'column',
                                position: 'relative',
                                gap: '16px',
                              }}
                              // eslint-disable-next-line react/no-array-index-key
                              key={`filter-${filterIdx}`}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  position: 'relative',
                                }}
                              >
                                <Typography.Text style={{
                                  color: '#8c909b',
                                  fontSize: '14px',
                                  lineHeight: '17px',
                                  marginBottom: '5px',
                                }}
                                >
                                  Field
                                  {hasFieldError
                                      && (
                                        <span
                                          style={{
                                            color: 'red',
                                            paddingLeft: '5px',
                                          }}
                                        >
                                          {fErrors[filterIdx].field}
                                        </span>
                                      )}
                                </Typography.Text>
                                <Select
                                  disabled={notEditable}
                                  name={`filters.${filterIdx}.field`}
                                  size="large"
                                  onChange={async (field) => {
                                    await reportActions.getReportFieldSuggestions(
                                      reportCollection,
                                      field,
                                    );
                                  }}
                                >
                                  {filterFields?.length && (
                                    filterFields.map((field, ffIdx) => (
                                      <Option
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={`${field.value}_${field.text}_${ffIdx}`}
                                        value={field.value}
                                      >
                                        {field.text}
                                      </Option>
                                    ))
                                  )}
                                </Select>
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  position: 'relative',
                                }}
                              >
                                <Typography.Text style={{
                                  color: '#8c909b',
                                  fontSize: '14px',
                                  lineHeight: '17px',
                                  marginBottom: '5px',
                                }}
                                >
                                  Type
                                  {hasTypeError
                                    && (
                                      <span style={{
                                        color: 'red',
                                        paddingLeft: '5px',
                                      }}
                                      >
                                        {fErrors[filterIdx].type}
                                      </span>
                                    )}
                                </Typography.Text>
                                <Select
                                  disabled={notEditable}
                                  name={`filters.${filterIdx}.type`}
                                  size="large"
                                >
                                  {operators?.length && (
                                    operators.map((item, oIdx) => (
                                      <Option
                                        value={item.value}
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={`${item.value}_${oIdx}`}
                                      >
                                        {item.text}
                                      </Option>
                                    ))
                                  )}
                                </Select>
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  position: 'relative',
                                }}
                              >
                                <Typography.Text style={{
                                  color: '#8c909b',
                                  fontSize: '14px',
                                  lineHeight: '17px',
                                  marginBottom: '5px',
                                }}
                                >
                                  Value
                                  {hasValueError
                                    && (
                                      <span
                                        style={{
                                          color: 'red',
                                          paddingLeft: '5px',
                                        }}
                                      >
                                        {fErrors[filterIdx].value}
                                      </span>
                                    )}
                                </Typography.Text>
                                <div style={{ display: 'flex' }}>
                                  <AutoComplete
                                    disabled={notEditable}
                                    name={`filters.${filterIdx}.value`}
                                    options={filterSuggestions}
                                    style={{ width: '88%' }}
                                    className="map-filter-value-autocomplete"
                                    onChange={(option: string) => {
                                      defineSuggestions(values, filterIdx)(option);
                                    }}
                                    onSearch={
                                      (criteria: string) => (
                                        debouncedSuggestionsSearch(
                                          criteria, filterIdx, values.filters,
                                        )
                                      )
                                    }
                                    onFocus={() => {
                                      if (values.filters) {
                                        defineSuggestions(values, filterIdx)(
                                          values.filters[filterIdx].value,
                                        );
                                      }
                                    }}
                                  />

                                  <button
                                    disabled={notEditable}
                                    type="button"
                                    onClick={() => {
                                      filtersArrayHelpers.remove(filterIdx);
                                    }}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    {notEditable ? '' : <ThrashCan /> }
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
              </FieldArrayRef>
            )}
          </FieldArray>
          {renderFiltersExtraButtons(values, setFieldValue)}
        </Col>
      </Row>
      <Row>
        <Col span="24" style={{ padding: '24px' }}>
          <div>
            <div>
              {renderAdditionalButtons && renderAdditionalButtons()}
              <Button
                type="primary"
                style={{ width: '100%' }}
                onClick={() => handleSubmit()}
                disabled={(
                  // (!submitButtonAlwaysEnabled && !dirty && !isValid) || isSubmitting
                  !submitButtonAlwaysEnabled && (!dirty)
                ) || notEditable}
              >
                {submitButtonLabel}
              </Button>
            </div>
          </div>
        </Col>
      </Row>
    </Form>
  );
};

FilterComponent.displayName = 'ReportParams';
export default FilterComponent;
